import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocFromCache,
  getDocFromServer
} from 'firebase/firestore';
import { useFirestoreCacheStatus } from './stores';
import { 
  ref, 
  uploadBytes, 
  uploadString,
  getDownloadURL, 
  deleteObject,
  listAll,
  list
} from 'firebase/storage';
import { db, storage } from "./firebase.config";
import { getCollectionPath, getSettingsPath } from './utils/dbPaths';

// ==================== FIRESTORE OPERATIONS ====================

/**
 * Get a single document from Firestore
 * @param {string} collectionName - Name of the collection
 * @param {string} documentId - ID of the document
 * @returns {Promise<Object|null>} Document data or null if not found
 */
export const getDocument = async (collectionName, documentId) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting document:', error);
    throw error;
  }
};

/**
 * Get multiple documents from a collection
 * @param {string} collectionName - Name of the collection
 * @param {Object} options - Query options (where, orderBy, limit)
 * @returns {Promise<Array>} Array of documents
 */
export const getDocuments = async (collectionName, options = {}) => {
  try {
    const colRef = collection(db, collectionName);
    let q = colRef;

    // Apply query constraints if provided
    const constraints = [];
    
    if (options.where) {
      constraints.push(where(options.where.field, options.where.operator, options.where.value));
    }
    
    if (options.orderBy) {
      constraints.push(orderBy(options.orderBy.field, options.orderBy.direction || 'asc'));
    }
    
    if (options.limit) {
      constraints.push(limit(options.limit));
    }

    if (constraints.length > 0) {
      q = query(colRef, ...constraints);
    }

    const querySnapshot = await getDocs(q);
    const documents = [];
    
    querySnapshot.forEach((doc) => {
      documents.push({ id: doc.id, ...doc.data() });
    });
    
    return documents;
  } catch (error) {
    console.error('Error getting documents:', error);
    throw error;
  }
};

/**
 * Create a new document with auto-generated ID
 * @param {string} collectionName - Name of the collection
 * @param {Object} data - Document data
 * @returns {Promise<string>} ID of the created document
 */
export const createDocument = async (collectionName, data) => {
  try {
    const colRef = collection(db, collectionName);
    const docRef = await addDoc(colRef, {
      ...data,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating document:', error);
    throw error;
  }
};

/**
 * Set a document with a specific ID (creates or overwrites)
 * @param {string} collectionName - Name of the collection
 * @param {string} documentId - ID of the document
 * @param {Object} data - Document data
 * @param {boolean} merge - Whether to merge with existing data
 * @returns {Promise<void>}
 */
export const setDocument = async (collectionName, documentId, data, merge = false) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    await setDoc(docRef, {
      ...data,
      updatedAt: new Date()
    }, { merge });
  } catch (error) {
    console.error('Error setting document:', error);
    throw error;
  }
};

/**
 * Update an existing document
 * @param {string} collectionName - Name of the collection
 * @param {string} documentId - ID of the document
 * @param {Object} data - Fields to update
 * @returns {Promise<void>}
 */
export const updateDocument = async (
  collectionName,
  documentId,
  data,
  addTimestamp = true
) => {
  try {
    // Handle full paths (e.g., "tenants/1234/stores/999/users") by splitting
    // Ensure collectionName is a string and all segments are valid strings
    if (!collectionName || typeof collectionName !== 'string') {
      throw new Error(`Invalid collection path: expected string, got ${typeof collectionName}`);
    }
    
    const pathSegments = collectionName.split('/')
      .map(segment => String(segment).trim())
      .filter(segment => segment.length > 0);
    
    if (pathSegments.length === 0) {
      throw new Error("Invalid collection path: empty path after filtering");
    }
    
    // Ensure documentId is a string (convert if needed)
    const documentIdString = String(documentId);
    if (!documentIdString || documentIdString.length === 0) {
      throw new Error("Invalid document ID: empty or undefined");
    }
    
    const docRef = doc(db, ...pathSegments, documentIdString);

    // If addTimestamp is false, just use the data as-is (for increment operations)
    const updateData = addTimestamp ? { ...data, updatedAt: new Date() } : data;

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error("Error updating document:", error);
    throw error;
  }
};

/** Allowed fields for direct booking edit (no payments / normal flow). */
const DIRECT_BOOKING_EDIT_FIELDS = [
  'startDate',
  'endDate',
  'customerPhone',
  'deliveryAddress',
  'addressNotes',
  'customerEmail',
  'gatedCommunity',
  'delivery',
  'homePickup',
  'product1',
  'product2',
  'adminNotes',
];

/**
 * Update only allowed booking fields on a booking document (app-side only, no Cloud Functions).
 * Use for staff direct edits without going through payment/booking flow.
 * @param {string} collectionPath - Full path e.g. from getCollectionPath('bookings') or getCollectionPath('completedBookings')
 * @param {string} bookingId - Document ID
 * @param {Object} fields - Object with only allowed keys: startDate, endDate, customerPhone, deliveryAddress, addressNotes, customerEmail (dates as Firestore Timestamp)
 * @returns {Promise<void>}
 */
export const updateBookingFieldsDirect = async (collectionPath, bookingId, fields) => {
  const allowed = {};
  for (const key of DIRECT_BOOKING_EDIT_FIELDS) {
    if (fields[key] !== undefined) {
      allowed[key] = fields[key];
    }
  }
  if (Object.keys(allowed).length === 0) {
    return;
  }
  await updateDocument(collectionPath, bookingId, allowed);
};

/**
 * Delete a document
 * @param {string} collectionName - Name of the collection
 * @param {string} documentId - ID of the document
 * @returns {Promise<void>}
 */
export const deleteDocument = async (collectionName, documentId) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time updates from a collection
 * @param {string} collectionName - Name of the collection
 * @param {Function} callback - Callback function to handle updates
 * @param {Object} options - Query options (where, orderBy, limit)
 * @returns {Function} Unsubscribe function
 */
export const subscribeToCollection = (collectionName, callback, options = {}) => {
  try {
    const colRef = collection(db, collectionName);
    let q = colRef;

    // Apply query constraints if provided
    const constraints = [];
    
    if (options.where) {
      constraints.push(where(options.where.field, options.where.operator, options.where.value));
    }
    
    if (options.orderBy) {
      constraints.push(orderBy(options.orderBy.field, options.orderBy.direction || 'asc'));
    }
    
    if (options.limit) {
      constraints.push(limit(options.limit));
    }

    if (constraints.length > 0) {
      q = query(colRef, ...constraints);
    }

    // Track if this is the first snapshot (to avoid showing banner on initial load)
    let isFirstSnapshot = true;

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (querySnapshot) => {
        // Track cache status for UI banner
        const isFromCache = querySnapshot.metadata.fromCache;
        const hasPendingWrites = querySnapshot.metadata.hasPendingWrites;
        
        const setServingCache = useFirestoreCacheStatus.getState().setServingCache;
        
        // Don't show banner on first snapshot (initial load)
        if (isFirstSnapshot) {
          setServingCache(false);
          isFirstSnapshot = false;
        } else if (isFromCache && !hasPendingWrites) {
          setServingCache(true);
        } else {
          setServingCache(false);
        }
        
        // Build documents array from snapshot
        const documents = [];
        querySnapshot.forEach((docSnap) => {
          documents.push({ id: docSnap.id, ...docSnap.data() });
        });

        callback(documents);
      },
      (error) => {
        console.error("Error in subscription:", error);
        callback([], error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up subscription:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time updates for a single document
 * @param {string} collectionName - Name of the collection
 * @param {string} documentId - ID of the document
 * @param {Function} callback - Callback function to handle updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToDocument = (collectionName, documentId, callback) => {
  try {
    const docRef = doc(db, collectionName, documentId);

    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() };
          callback(data);
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error('Error in document subscription:', error);
        callback(null, error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up document subscription:', error);
    throw error;
  }
};

// ==================== CLOUD STORAGE OPERATIONS ====================

/**
 * Upload a file to Cloud Storage
 * @param {string} path - Storage path (e.g., 'photos/user123/image.jpg')
 * @param {File|Blob} file - File or Blob to upload
 * @param {Object} metadata - Optional metadata
 * @returns {Promise<string>} Download URL of the uploaded file
 */
export const uploadFile = async (path, file, metadata = {}) => {
  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file, metadata);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Upload a base64 string to Cloud Storage
 * @param {string} path - Storage path
 * @param {string} base64String - Base64 encoded string
 * @param {string} format - Data format (e.g., 'data_url', 'base64')
 * @param {Object} metadata - Optional metadata
 * @returns {Promise<string>} Download URL of the uploaded file
 */
export const uploadBase64 = async (path, base64String, format = 'data_url', metadata = {}) => {
  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadString(storageRef, base64String, format, metadata);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading base64:', error);
    throw error;
  }
};

/**
 * Get download URL for a file
 * @param {string} path - Storage path
 * @returns {Promise<string>} Download URL
 */
export const getFileURL = async (path) => {
  try {
    const storageRef = ref(storage, path);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    console.error('Error getting file URL:', error);
    throw error;
  }
};

/**
 * Delete a file from Cloud Storage
 * @param {string} path - Storage path
 * @returns {Promise<void>}
 */
export const deleteFile = async (path) => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * List files in a storage path
 * @param {string} path - Storage path (e.g., 'customers/123/photos')
 * @param {number} maxResults - Maximum number of results
 * @returns {Promise<Array>} Array of file references with metadata
 */
export const listFiles = async (path, maxResults = 100) => {
  try {
    const storageRef = ref(storage, path);
    const listResult = await listAll(storageRef);
    
    // Get URLs for all items
    const filesWithUrls = await Promise.all(
      listResult.items.slice(0, maxResults).map(async (itemRef) => {
        const url = await getDownloadURL(itemRef);
        return {
          name: itemRef.name,
          fullPath: itemRef.fullPath,
          url: url,
          ref: itemRef
        };
      })
    );
    
    return filesWithUrls;
  } catch (error) {
    console.error('Error listing files:', error);
    // Return empty array if path doesn't exist
    if (error.code === 'storage/object-not-found') {
      return [];
    }
    throw error;
  }
};

